import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { DashboardLayout } from "components/DashboardLayout";
import { TableUI } from "components/design/TableUI";
import { usePageTitle } from "hooks/usePageTitle";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { ReactNode, useState } from "react";
import { useParams } from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  CouponStripeCoupon,
  CouponTaskCategoryMapping,
  useDeleteCouponStripeCouponMutation,
  useDeleteCouponTaskCategoryMappingMutation,
  useGetAdminCouponOptionsQuery,
  useGetAdminCouponQuery,
  useUpdateAdminCouponMutation,
} from "store/apis/couponAdmin";
import { CouponFormModal } from "./CouponFormModal";
import { CouponStatusTag } from "./Coupons";
import {
  formatDate,
  formatDiscount,
  getCouponSignupLink,
  getErrorMessage,
} from "./couponUtils";
import { DeleteMappingModal } from "./DeleteMappingModal";
import { StripeCouponModal } from "./StripeCouponModal";
import { TaskCategoryMappingModal } from "./TaskCategoryMappingModal";

type PendingDelete =
  | { kind: "task"; mapping: CouponTaskCategoryMapping }
  | { kind: "stripe"; mapping: CouponStripeCoupon };

const Section = ({
  title,
  description,
  onAdd,
  children,
}: {
  title: string;
  description: string;
  onAdd: () => void;
  children: ReactNode;
}) => (
  <div className="t-flex t-flex-col t-gap-3">
    <div className="t-flex t-justify-between t-items-start">
      <div>
        <div className="t-text-subtitle t-text-text-100">{title}</div>
        <div className="t-text-body-sm t-text-text-30">{description}</div>
      </div>
      <Button size="small" onClick={onAdd}>
        Add
      </Button>
    </div>
    {children}
  </div>
);

const RowActions = ({
  onEdit,
  onRemove,
}: {
  onEdit: () => void;
  onRemove: () => void;
}) => (
  <div className="t-flex t-gap-2 t-justify-end">
    <Button size="small" customType="ghost" onClick={onEdit}>
      Edit
    </Button>
    <Button size="small" customType="ghost" onClick={onRemove}>
      Remove
    </Button>
  </div>
);

const EmptyRow = ({ text }: { text: string }) => (
  <div className="t-border t-border-dashed t-border-neutral-10 t-rounded t-p-4 t-text-body-sm t-text-text-30 t-text-center">
    {text}
  </div>
);

export const CouponDetail = () => {
  const { couponId } = useParams<{ couponId: string }>();
  const { alertToast, successToast } = useToast();
  const { copyToClipboard } = useCopyToClipboard();
  const editModal = useModal();
  const taskModal = useModal();
  const stripeModal = useModal();
  const deleteModal = useModal();

  const [editingTaskMapping, setEditingTaskMapping] =
    useState<CouponTaskCategoryMapping>();
  const [editingStripeCoupon, setEditingStripeCoupon] =
    useState<CouponStripeCoupon>();
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null
  );

  const {
    data: coupon,
    isLoading,
    isSuccess,
  } = useGetAdminCouponQuery({
    couponId,
  });
  const { data: options } = useGetAdminCouponOptionsQuery();
  const [updateCoupon, { isLoading: isTogglingActive }] =
    useUpdateAdminCouponMutation();
  const [deleteTaskMapping, { isLoading: isDeletingTask }] =
    useDeleteCouponTaskCategoryMappingMutation();
  const [deleteStripeCoupon, { isLoading: isDeletingStripe }] =
    useDeleteCouponStripeCouponMutation();

  usePageTitle(coupon ? `Coupon ${coupon.coupon_code}` : "Coupon");

  const toggleActive = async () => {
    if (!coupon) {
      return;
    }
    try {
      await updateCoupon({
        couponId,
        payload: { is_active: !coupon.is_active },
      }).unwrap();
      successToast({
        message: coupon.is_active ? "Coupon deactivated" : "Coupon activated",
      });
    } catch (err) {
      alertToast({ message: getErrorMessage(err) });
    }
  };

  const openTaskModal = (mapping?: CouponTaskCategoryMapping) => {
    setEditingTaskMapping(mapping);
    taskModal.open();
  };

  const openStripeModal = (stripeCoupon?: CouponStripeCoupon) => {
    setEditingStripeCoupon(stripeCoupon);
    stripeModal.open();
  };

  const openDeleteModal = (target: PendingDelete) => {
    setPendingDelete(target);
    deleteModal.open();
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    const args = { couponId, mappingId: pendingDelete.mapping.uuid };
    try {
      if (pendingDelete.kind === "task") {
        await deleteTaskMapping(args).unwrap();
      } else {
        await deleteStripeCoupon(args).unwrap();
      }
      successToast({ message: "Removed" });
      deleteModal.close();
    } catch (err) {
      alertToast({ message: getErrorMessage(err) });
    }
  };

  const taskColumnHelper = createColumnHelper<CouponTaskCategoryMapping>();
  const taskTable = useReactTable({
    data: coupon?.task_category_mappings ?? [],
    columns: [
      taskColumnHelper.accessor("task_template_category.title", {
        header: "Category",
        size: 25,
      }),
      taskColumnHelper.display({
        id: "discount",
        header: "Discount",
        size: 15,
        cell: ({ row }) =>
          formatDiscount(
            row.original.discount_type,
            row.original.discount_rate
          ),
      }),
      taskColumnHelper.display({
        id: "expiry",
        header: "Expiry",
        size: 20,
        cell: ({ row }) => formatDate(row.original.expiry),
      }),
      taskColumnHelper.display({
        id: "free_task",
        header: "Free task",
        size: 20,
        cell: ({ row }) =>
          row.original.is_free_task_coupon
            ? `Yes (${row.original.allowed_redemption ?? "∞"} redemptions)`
            : "No",
      }),
      taskColumnHelper.display({
        id: "actions",
        header: "",
        size: 20,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => openTaskModal(row.original)}
            onRemove={() =>
              openDeleteModal({ kind: "task", mapping: row.original })
            }
          />
        ),
      }),
    ],
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 1 },
  });

  const stripeColumnHelper = createColumnHelper<CouponStripeCoupon>();
  const stripeTable = useReactTable({
    data: coupon?.stripe_coupons ?? [],
    columns: [
      stripeColumnHelper.accessor("stripe_coupon_id", {
        header: "Stripe coupon ID",
        size: 20,
      }),
      stripeColumnHelper.accessor("subscription_plan.name", {
        header: "Plan",
        size: 25,
      }),
      stripeColumnHelper.display({
        id: "discount",
        header: "Discount",
        size: 15,
        cell: ({ row }) =>
          formatDiscount(
            row.original.discount_type,
            row.original.discount_rate
          ),
      }),
      stripeColumnHelper.display({
        id: "duration",
        header: "Duration",
        size: 20,
        cell: ({ row }) =>
          row.original.duration_type === "FOREVER"
            ? "Forever"
            : `${row.original.duration_period} months`,
      }),
      stripeColumnHelper.display({
        id: "actions",
        header: "",
        size: 20,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => openStripeModal(row.original)}
            onRemove={() =>
              openDeleteModal({ kind: "stripe", mapping: row.original })
            }
          />
        ),
      }),
    ],
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 1 },
  });

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title={coupon?.coupon_code ?? "Coupon"}
          breadcrumbs={[
            { name: "Coupons", link: "/admin/coupons" },
            { name: coupon?.coupon_code ?? "" },
          ]}
          right={
            coupon && (
              <div className="t-flex t-gap-2">
                <Button
                  size="small"
                  onClick={() =>
                    copyToClipboard(getCouponSignupLink(coupon.coupon_code))
                  }
                >
                  Copy signup link
                </Button>
                <Button size="small" onClick={editModal.open}>
                  Edit
                </Button>
                <Button
                  size="small"
                  customType={coupon.is_active ? "danger" : "primary"}
                  onClick={toggleActive}
                  isLoading={isTogglingActive}
                  disabled={isTogglingActive}
                >
                  {coupon.is_active ? "Deactivate" : "Activate"}
                </Button>
              </div>
            )
          }
        />
      }
    >
      <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={false}>
        <Async.Success>
          {coupon && (
            <div className="t-flex t-flex-col t-gap-8 t-pb-8">
              <div className="t-grid t-grid-cols-3 t-gap-4 t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-4">
                <InfoItem label="Status">
                  <CouponStatusTag coupon={coupon} />
                </InfoItem>
                <InfoItem label="Validity">
                  {formatDate(coupon.valid_from)} –{" "}
                  {formatDate(coupon.valid_till)}
                </InfoItem>
                <InfoItem label="Max discount per group">
                  ${coupon.max_discount}
                </InfoItem>
                <InfoItem label="Free task coupon">
                  {coupon.is_free_task_coupon ? "Yes" : "No"}
                </InfoItem>
                <InfoItem
                  label="Signup link"
                  valueClassName="t-text-body-sm t-break-all"
                >
                  {getCouponSignupLink(coupon.coupon_code)}
                </InfoItem>
                <InfoItem label="Description">{coupon.description}</InfoItem>
              </div>

              <Section
                title="Task discounts"
                description="Discounts on filings by task template category, e.g. DFT, F1099, FBAR, 7004."
                onAdd={() => openTaskModal()}
              >
                {coupon.task_category_mappings.length ? (
                  <TableUI table={taskTable} layout="fixed" />
                ) : (
                  <EmptyRow text="No task discounts yet" />
                )}
              </Section>

              <Section
                title="Stripe coupons"
                description="Subscription discounts applied at Stripe checkout, one per plan."
                onAdd={() => openStripeModal()}
              >
                {coupon.stripe_coupons.length ? (
                  <TableUI table={stripeTable} layout="fixed" />
                ) : (
                  <EmptyRow text="No Stripe coupons yet" />
                )}
              </Section>
            </div>
          )}
        </Async.Success>
      </Async.Root>

      {coupon && (
        <CouponFormModal
          isOpen={editModal.isOpen}
          close={editModal.close}
          coupon={coupon}
        />
      )}
      <TaskCategoryMappingModal
        isOpen={taskModal.isOpen}
        close={taskModal.close}
        couponId={couponId}
        categories={options?.task_template_categories ?? []}
        mapping={editingTaskMapping}
      />
      <StripeCouponModal
        isOpen={stripeModal.isOpen}
        close={stripeModal.close}
        couponId={couponId}
        plans={options?.subscription_plans ?? []}
        stripeCoupon={editingStripeCoupon}
      />
      {pendingDelete && (
        <DeleteMappingModal
          isOpen={deleteModal.isOpen}
          close={deleteModal.close}
          title={
            pendingDelete.kind === "task"
              ? "Remove task discount?"
              : "Remove Stripe coupon?"
          }
          description={
            pendingDelete.kind === "task"
              ? `${pendingDelete.mapping.task_template_category.title} filings will no longer be discounted by this coupon.`
              : `New ${pendingDelete.mapping.subscription_plan.name} subscriptions will no longer get this Stripe coupon.`
          }
          isLoading={isDeletingTask || isDeletingStripe}
          onConfirm={confirmDelete}
        />
      )}
    </DashboardLayout>
  );
};
